import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import { fetchFormData } from '../../../Api/ApiMethods';
import { NAME_TR_REGEX, validateEmail, validatePhoneTr, validateTcid } from '../../../_helper/regexHandler';
import { useModalCrudState } from '../../../_helper/hooks/useModalCrudState';

const EmployeeDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedEmployee, refreshData }) => {
    const initialEmployeeData = {
        id: '',
        roleId: '',
        personId: '',
        firstName: '',
        lastName: '',
        tcId: '',
        job: '',
        department: '',
        phoneNumber: '',
        email: '',
        employmentStartDate: '',
        isBlocked: false
    };

    const customHandlers = useMemo(() => ({
        isBlocked: (value, prev) => ({ ...prev, isBlocked: value === 'true' }),
    }), []);

    const validationSchema = useMemo(() => ({
        roleId: { when: () => mode === 'modify', required: true, message: 'Rol seçimi zorunludur.' },
        firstName: { required: true, message: 'Çalışan adı zorunludur.', regex: NAME_TR_REGEX, regexMessage: 'Ad sadece harf ve boşluk içerebilir.' },
        lastName: { regex: NAME_TR_REGEX, regexMessage: 'Soyad sadece harf ve boşluk içerebilir.' },
        tcId: { required: true, message: 'Kimlik numarası zorunludur.', validator: validateTcid, validatorMessage: 'TC Kimlik Numarası 11 haneli rakam olmalıdır.' },
        phoneNumber: { validator: validatePhoneTr, validatorMessage: 'Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır.' },
        email: { conditionalRequired: () => mode === 'add', message: 'E-posta adresi zorunludur.', validator: validateEmail, validatorMessage: 'Geçerli bir e-posta adresi giriniz.' },
        job: { minLength: 2, minLengthMessage: 'İş alanı en az 2 karakter olmalıdır.' },
        department: { minLength: 2, minLengthMessage: 'Departman en az 2 karakter olmalıdır.' },
    }), [mode]);

    const registerEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Account/register';

    const {
        formData: employeeData,
        errors,
        handleCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedEmployee,
        initialData: initialEmployeeData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            let currentRoleId = '';
            if (selectedItem?.roles && typeof selectedItem.roles === 'object') {
                const roleIds = Object.keys(selectedItem.roles);
                if (roleIds.length > 0) {
                    currentRoleId = String(roleIds[0]);
                }
            } else if (selectedItem?.roleId) {
                currentRoleId = String(selectedItem.roleId);
            }

            const formattedStartDate = selectedItem?.employmentStartDate?.split('T')[0] || '';

            return {
                ...initialData,
                id: selectedItem?.id,
                personId: selectedItem?.personId,
                firstName: selectedItem?.firstName,
                lastName: selectedItem?.lastName,
                tcId: selectedItem?.tcId,
                job: selectedItem?.job,
                department: selectedItem?.department,
                phoneNumber: selectedItem?.phoneNumber,
                email: selectedItem?.email,
                isBlocked: selectedItem?.isBlocked,
                roleId: currentRoleId,
                employmentStartDate: formattedStartDate,
            };
        },
        normalizeInput: true,
        customHandlers,
        validationSchema,
        submitConfig: {
            apiEndpoint: mode === 'add' ? registerEndpoint : apiEndpoint,
            refreshData,
            componentName: 'Çalışan',
            httpMethod: mode === 'modify' ? 'PATCH' : undefined,
            buildPayload: (data) => {
                const formattedStartDate = data.employmentStartDate
                    ? new Date(data.employmentStartDate).toISOString()
                    : null;

                if (mode === 'add') {
                    return {
                        firstName: data.firstName,
                        lastName: data.lastName,
                        username: data.email,
                        email: data.email,
                        password: data.tcId,
                        tcId: data.tcId,
                        job: data.job,
                        department: data.department,
                        phoneNumber: data.phoneNumber,
                        employmentStartDate: formattedStartDate,
                        isBlocked: data.isBlocked
                    };
                }

                return {
                    id: data.id,
                    personId: data.personId,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    tcId: data.tcId,
                    job: data.job,
                    department: data.department,
                    phoneNumber: data.phoneNumber,
                    email: data.email,
                    employmentStartDate: formattedStartDate,
                    isBlocked: data.isBlocked,
                    roles: data.roleId ? [data.roleId] : []
                };
            },
        },
    });

    const [roleOptions, setRoleOptions] = useState([]);
    const roleApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/role';

    // Modal açıldığında rol listesini yükler
    useEffect(() => {
        const loadRoles = async () => {
            if (isOpen && roleApiUrl) {
                try {
                    const response = await fetchFormData({ apiEndpoint: roleApiUrl });

                    let roles = [];
                    if (Array.isArray(response)) {
                        roles = response;
                    } else if (Array.isArray(response?.items)) {
                        roles = response.items;
                    } else if (Array.isArray(response?.data?.items)) {
                        roles = response.data.items;
                    } else if (Array.isArray(response?.data)) {
                        roles = response.data;
                    }

                    const normalizedRoles = roles
                        .filter(role => role?.id)
                        .map(role => ({
                            id: String(role.id),
                            name: role.name || role.title || '-'
                        }));

                    setRoleOptions(normalizedRoles);
                } catch {
                    setRoleOptions([]);
                }
            }
        };
        loadRoles();
    }, [isOpen, roleApiUrl]);

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>
                {mode === 'add' ? 'Çalışan Ekle' : 'Çalışan Düzenle'}
            </ModalHeader>
            <ModalBody>
                <Form>
                    {mode === 'modify' && (
                        <FormGroup>
                            <Label for="roleId">Rol <span className="text-danger">*</span></Label>
                            <Input
                                type="select"
                                id="roleId"
                                name="roleId"
                                value={employeeData.roleId || ''}
                                onChange={handleInputChange}
                                invalid={!!errors.roleId}
                            >
                                <option value="">Rol Seçiniz...</option>
                                {roleOptions.map(role => (
                                    <option key={role.id} value={role.id}>
                                        {role.name}
                                    </option>
                                ))}
                            </Input>
                            {errors.roleId && <FormFeedback>{errors.roleId}</FormFeedback>}
                        </FormGroup>
                    )}
                    {mode === 'modify' && (
                        <FormGroup>
                            <Label for="personId">Çalışan ID</Label>
                            <Input
                                type="text"
                                id="personId"
                                name="personId"
                                value={employeeData.personId || ''}
                                placeholder="Çalışan ID (Sistem Tarafından Atanır)"
                                readOnly
                                disabled
                            />
                        </FormGroup>
                    )}
                    <FormGroup>
                        <Label for="firstName">Ad <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={employeeData.firstName || ''}
                            onChange={handleInputChange}
                            placeholder="Ad"
                            invalid={!!errors.firstName}
                        />
                        <FormFeedback>{errors.firstName}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="lastName">Soyad <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={employeeData.lastName || ''}
                            onChange={handleInputChange}
                            placeholder="Soyad"
                            invalid={!!errors.lastName}
                        />
                        <FormFeedback>{errors.lastName}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="tcId">Kimlik Numarası <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="tcId"
                            name="tcId"
                            value={employeeData.tcId || ''}
                            onChange={handleInputChange}
                            placeholder="11 Haneli TC Kimlik No"
                            maxLength={11}
                            invalid={!!errors.tcId}
                        />
                        <FormFeedback>{errors.tcId}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="job">İş / Pozisyon</Label>
                        <Input
                            type="text"
                            id="job"
                            name="job"
                            value={employeeData.job || ''}
                            onChange={handleInputChange}
                            placeholder="Çalışanın Pozisyonu"
                            invalid={!!errors.job}
                        />
                        <FormFeedback>{errors.job}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="department">Departman</Label>
                        <Input
                            type="text"
                            id="department"
                            name="department"
                            value={employeeData.department || ''}
                            onChange={handleInputChange}
                            placeholder="Çalıştığı Departman"
                            invalid={!!errors.department}
                        />
                        <FormFeedback>{errors.department}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="phoneNumber">Telefon Numarası</Label>
                        <Input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={employeeData.phoneNumber || ''}
                            onChange={handleInputChange}
                            placeholder="05XX XXX XX XX"
                            maxLength={11}
                            invalid={!!errors.phoneNumber}
                        />
                        <FormFeedback>{errors.phoneNumber}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="email">E-posta Adresi {mode === 'add' && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="email"
                            id="email"
                            name="email"
                            value={employeeData.email || ''}
                            onChange={handleInputChange}
                            placeholder="ornek@sirket.com"
                            invalid={!!errors.email}
                        />
                        <FormFeedback>{errors.email}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="employmentStartDate">İşe Başlama Tarihi</Label>
                        <Input
                            type="date"
                            id="employmentStartDate"
                            name="employmentStartDate"
                            value={employeeData.employmentStartDate || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.employmentStartDate}
                        />
                        <FormFeedback>{errors.employmentStartDate}</FormFeedback>
                    </FormGroup>
                    <FormGroup className="d-flex align-items-center mt-4 mb-3">
                        <Label className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Hesap Durumu:</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-danger me-3">
                                <Input
                                    className="form-check-input"
                                    id="isBlockedTrue"
                                    type="radio"
                                    name="isBlocked"
                                    value="true"
                                    checked={employeeData.isBlocked === true}
                                    onChange={handleInputChange}
                                />
                                <Label className="form-check-label" htmlFor="isBlockedTrue">Bloklu</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-success">
                                <Input
                                    className="form-check-input"
                                    id="isBlockedFalse"
                                    type="radio"
                                    name="isBlocked"
                                    value="false"
                                    checked={employeeData.isBlocked === false}
                                    onChange={handleInputChange}
                                />
                                <Label className="form-check-label" htmlFor="isBlockedFalse">Aktif</Label>
                            </div>
                            {errors.isBlocked && <FormFeedback className="d-block">{errors.isBlocked}</FormFeedback>}
                        </div>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="outline-success" className="rounded-pill" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? 'Kaydediliyor...' : (mode === 'add' ? 'Ekle' : 'Güncelle')}
                </Button>
                <Button color="outline-danger" onClick={handleCancel} className="ms-2 rounded-pill" disabled={isSubmitting}>
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EmployeeDataModal;
